import React, { useState } from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { Box, BoxProps } from '@withjoy/joykit';
import { LocationInputV2Response } from '../LocationInputV2/LocationInputV2.types';
import { usePlaceholderCycler } from './hooks/usePlaceholderCycler';

export interface AdvancedLocationInputV2FormFields
  extends Readonly<{
    placeId: string;
    locationValue: string;
    displayName?: string;
    latitude?: number | null;
    longitude?: number | null;
    address?: string;
    formattedAddress?: string;
    city?: string;
    stateProvince?: string;
    country?: string;
  }> {}

type PropsInjectedByHOC = Readonly<{
  placeholder: string;
  defaultValue?: string;
  loading: boolean;
  disabled: boolean;
  isInvalid: boolean;
  onInputChange?: (val: string) => void;
  onLocationOpen?: () => void;
  onLocationChange?: (val: LocationInputV2Response | null) => void;
}>;

interface WithFormikLocationInputV2Props
  extends Readonly<{
    name: string;
    placeholders: string[];
    initialValues: AdvancedLocationInputV2FormFields;
    children?: React.ReactNode;
    errorProps?: BoxProps;
    onSubmit?: (values: AdvancedLocationInputV2FormFields) => void;
    onChangeLocation?: (values: AdvancedLocationInputV2FormFields) => void;
    expandedViewTitle?: string;
  }> {}

const DEFAULT_VALUES = { placeId: '', locationValue: '' };

/**
 * Generic HOC that enhances a location input component with formik, validation, and Google place info support.
 */
export function withFormikLocationInputV2<T extends {}>(
  WrappedComponent: React.ComponentType<T>,
): React.FC<Omit<T, keyof PropsInjectedByHOC> & Omit<WithFormikLocationInputV2Props, keyof PropsInjectedByHOC>> {
  return function AdvancedLocationInputComponent(props) {
    const { placeholders, initialValues = DEFAULT_VALUES, children, onSubmit, onChangeLocation, errorProps, expandedViewTitle, ...restProps } = props;

    const [isLocationSelectedFromList, setIsLocationSelectedFromList] = useState(false);

    const currentPlaceholder = usePlaceholderCycler(placeholders);

    const { setFieldTouched, submitCount } = useFormikContext();
    const [field, meta, helpers] = useField<AdvancedLocationInputV2FormFields>(props.name);

    const showError = Boolean((meta.touched || submitCount > 0) && meta.error);
    let errorMessageToShow: string | undefined;
    if (typeof meta.error === 'string') {
      errorMessageToShow = meta.error;
    } else if (meta.error && typeof meta.error === 'object') {
      errorMessageToShow = getIn(meta.error, 'locationValue');
    }

    const handleSearch = ({ location }: { location: LocationInputV2Response | null }) => {
      if (!location?.placeId) return;

      const values: AdvancedLocationInputV2FormFields = {
        placeId: location.placeId,
        locationValue: location.displayName,
        displayName: location.displayName,
        address: location.address,
        formattedAddress: location.formattedAddress,
        city: location.city,
        latitude: location.lat,
        longitude: location.lng,
        stateProvince: location.stateRegionProvince,
        country: location.country,
      };

      helpers.setValue(values);
      onChangeLocation?.(values);
    };

    const loading = isLocationSelectedFromList && !(meta.value?.latitude && meta.value?.longitude);
    const disabled = !(meta.value?.latitude && meta.value?.longitude);

    const injectedProps: PropsInjectedByHOC = {
      placeholder: currentPlaceholder,
      defaultValue: initialValues.locationValue,
      loading,
      disabled,
      isInvalid: showError,
      onLocationOpen: () => {
        setFieldTouched(props.name, true);
        setIsLocationSelectedFromList(false);
      },
      onLocationChange: (location: LocationInputV2Response | null) => {
        handleSearch({ location });
        setIsLocationSelectedFromList(true);
      },
    };

    return (
      <Box width="100%">
        <WrappedComponent
          {...(restProps as unknown as T)}
          {...field}
          {...meta}
          {...(injectedProps as PropsInjectedByHOC)}
          expandedViewTitle={expandedViewTitle}
        >
          {children}
        </WrappedComponent>
        {showError && (
          <Box
            typographyVariant="label2"
            color="red6"
            marginTop={5}
            textAlign="center"
            {...errorProps}
          >
            {errorMessageToShow}
          </Box>
        )}
      </Box>
    );
  };
}
